import dayjs from 'dayjs'
import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { CellContext, createColumnHelper } from '@tanstack/react-table'
import Table from '@/components/ui/tables/Table'
import { PlanningOverviewDto } from '@/core/models/planning.model'
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { getPlanningOverviewCompletionColor, getPlanningOverviewStatusLabel } from '../../plannings-management/lib/helpers'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import VisibilityIcon from '@mui/icons-material/Visibility'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ArchiveIcon from '@mui/icons-material/Archive'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import DeleteIcon from '@mui/icons-material/Delete'
import { useArchivePlanningMutation, useDeletePlanningMutation, useUnarchivePlanningMutation } from '@/core/services/planning'
import { useSnackbar } from 'notistack'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCallbackRef } from '@/hooks/useCallbackRef'

type PlanningData = PlanningOverviewDto & { id: string }

const columnHelper = createColumnHelper<PlanningData>()

type PlanningsOverviewTableProps = {
  isLoading?: boolean
  onArchiveCompleted: (planningId: string) => void
  onUnarchiveCompleted: (planningId: string) => void
  onDeleteCompleted: (planningId: string) => void
  plannings: PlanningOverviewDto[]
}

export function PlanningsOverviewTable({
  isLoading,
  onArchiveCompleted: onArchiveCompletedProp,
  onUnarchiveCompleted: onUnarchiveCompletedProp,
  onDeleteCompleted,
  plannings,
}: PlanningsOverviewTableProps) {
  useLingui()
  const { enqueueSnackbar } = useSnackbar()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [planningToDelete, setPlanningToDelete] = useState<string | null>(null)

  const [deletePlanning, { isLoading: isDeleting }] = useDeletePlanningMutation()

  const onArchiveCompleted = useCallbackRef(onArchiveCompletedProp)
  const onUnarchiveCompleted = useCallbackRef(onUnarchiveCompletedProp)

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t`Name`,
      }),
      columnHelper.display({
        id: 'dateRange',
        header: t`Date range`,
        cell: ({ row }) => {
          const planning = row.original
          return `${dayjs(planning.startDate).format('MMM D')} - ${dayjs(planning.endDate).format('MMM D, YYYY')}`
        },
      }),
      columnHelper.accessor('status', {
        header: t`Status`,
        cell: StatusCell,
      }),
      columnHelper.accessor('totalShiftTemplates', {
        header: t`Templates`,
      }),
      columnHelper.accessor('completionRate', {
        header: t`Completion`,
        cell: CompletionCell,
      }),
      columnHelper.display({
        id: 'actions',
        header: t`Actions`,
        cell: (cellProps) => (
          <ActionsCell
            planning={cellProps.row.original}
            onArchiveCompleted={onArchiveCompleted}
            onUnarchiveCompleted={onUnarchiveCompleted}
            setPlanningToDelete={setPlanningToDelete}
          />
        ),
      }),
    ],
    [onArchiveCompleted, onUnarchiveCompleted]
  )

  const data = useMemo(() => plannings.map((planning) => ({ ...planning, id: planning._id })), [plannings])

  const handleConfirmDelete = async () => {
    if (!planningToDelete) return

    try {
      await deletePlanning(planningToDelete).unwrap()
      enqueueSnackbar(t`Planning deleted successfully`, { variant: 'success' })
      onDeleteCompleted?.(planningToDelete)
    } catch (error) {
      enqueueSnackbar(t`Failed to delete planning`, { variant: 'error' })
    } finally {
      setPlanningToDelete(null)
    }
  }

  return (
    <>
      <Paper>
        <Typography variant={isMobile ? 'subtitle1' : 'h5'} fontWeight={isMobile ? 700 : undefined} gutterBottom p={{ xs: 1.5, md: 3 }} pb={{ xs: 0.5, md: 1 }}>
          {t`Planning Overview`}
        </Typography>
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height={isMobile ? 180 : 400} />
        ) : isMobile ? (
          <Stack spacing={1} sx={{ px: 1.5, pb: 1.5 }}>
            {data.map((planning) => (
              <PlanningOverviewCard
                key={planning.id}
                planning={planning}
                onArchiveCompleted={onArchiveCompleted}
                onUnarchiveCompleted={onUnarchiveCompleted}
                setPlanningToDelete={setPlanningToDelete}
              />
            ))}
          </Stack>
        ) : (
          <Table columns={columns} data={data} disableGlobalSearch showPagination={false} />
        )}
      </Paper>
      <ConfirmDialog
        open={!!planningToDelete}
        title={t`Delete Planning`}
        content={t`Are you sure you want to delete this planning? This action cannot be undone.`}
        confirmText={t`Delete`}
        cancelText={t`Cancel`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPlanningToDelete(null)}
        confirmButtonProps={{ color: 'error' }}
      />
    </>
  )
}

function ArchiveAction({ planningId }: { onArchiveCompleted: (planningId: string) => void; planningId: string }) {
  useLingui()
  const { enqueueSnackbar } = useSnackbar()

  const [archivePlanning, { isLoading: isArchiving }] = useArchivePlanningMutation()

  const handleArchive = async () => {
    try {
      await archivePlanning(planningId).unwrap()
      enqueueSnackbar(t`Planning archived successfully`, { variant: 'success' })
      // handleRefresh()
    } catch (error) {
      enqueueSnackbar(t`Failed to archive planning`, { variant: 'error' })
    }
  }
  return (
    <MenuItem onClick={handleArchive}>
      <ListItemIcon>
        <ArchiveIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{t`Archive`}</ListItemText>
    </MenuItem>
  )
}

function UnarchiveAction({ onUnarchiveCompleted, planningId }: { onUnarchiveCompleted: (planningId: string) => void; planningId: string }) {
  useLingui()
  const { enqueueSnackbar } = useSnackbar()

  const [unarchivePlanning, { isLoading: isUnarchiving }] = useUnarchivePlanningMutation()

  const handleUnarchive = async () => {
    try {
      await unarchivePlanning(planningId).unwrap()
      enqueueSnackbar(t`Planning unarchived successfully`, { variant: 'success' })
      onUnarchiveCompleted(planningId)
    } catch (error) {
      enqueueSnackbar(t`Failed to unarchive planning`, { variant: 'error' })
    }
  }
  return (
    <MenuItem onClick={handleUnarchive}>
      <ListItemIcon>
        <UnarchiveIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{t`Unarchive`}</ListItemText>
    </MenuItem>
  )
}

function ActionsCell({
  planning,
  onArchiveCompleted,
  onUnarchiveCompleted,
  setPlanningToDelete,
}: {
  planning: PlanningData
  onArchiveCompleted: (planningId: string) => void
  onUnarchiveCompleted: (planningId: string) => void
  setPlanningToDelete: Dispatch<SetStateAction<string | null>>
}) {
  useLingui()
  const location = useLocation()
  const navigate = useNavigate()

  return (
    <Stack justifyContent="flex-end" direction="row" spacing={1} onClick={(event) => event.stopPropagation()}>
      <PopupState variant="popover" popupId="plannings-overview-table-actions">
        {(popupState) => (
          <>
            <Tooltip title={t`More Actions`}>
              <IconButton {...bindTrigger(popupState)} size="small">
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu {...bindMenu(popupState)} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <MenuItem onClick={() => navigate(`${location.pathname}?planningId=${planning.id}&tab=admin`)}>
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t`View planning`}</ListItemText>
              </MenuItem>
              {planning.archived ? (
                <UnarchiveAction onUnarchiveCompleted={onUnarchiveCompleted} planningId={planning.id} />
              ) : (
                <ArchiveAction onArchiveCompleted={onArchiveCompleted} planningId={planning.id} />
              )}
              <MenuItem onClick={() => setPlanningToDelete(planning.id)}>
                <ListItemIcon>
                  <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>{t`Delete`}</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </PopupState>
    </Stack>
  )
}

function CompletionCell({ row: { original: planning } }: CellContext<PlanningData, number>) {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <LinearProgress
        variant="determinate"
        value={planning.completionRate}
        sx={{
          width: 100,
          height: 8,
          borderRadius: 5,
          backgroundColor: 'grey.300',
          '& .MuiLinearProgress-bar': {
            backgroundColor: getPlanningOverviewCompletionColor(planning.completionRate),
          },
        }}
      />
      <Typography variant="body2">{Math.round(planning.completionRate)}%</Typography>
      <Typography variant="caption" color="text.secondary">
        ({planning.staffedShifts}/{planning.totalPossibleShifts})
      </Typography>
    </Stack>
  )
}

function StatusCell({ row: { original: planning } }: CellContext<PlanningData, string>) {
  useLingui()

  let color: 'success' | 'info' | 'default' = 'default'
  switch (planning.status) {
    case 'active':
      color = 'success'
      break
    case 'upcoming':
      color = 'info'
      break
  }

  const label = getPlanningOverviewStatusLabel(planning)

  return <Chip label={label} color={color} size="small" />
}

function PlanningOverviewCard({
  planning,
  onArchiveCompleted,
  onUnarchiveCompleted,
  setPlanningToDelete,
}: {
  planning: PlanningData
  onArchiveCompleted: (planningId: string) => void
  onUnarchiveCompleted: (planningId: string) => void
  setPlanningToDelete: Dispatch<SetStateAction<string | null>>
}) {
  useLingui()
  const location = useLocation()
  const navigate = useNavigate()

  let statusColor: 'success' | 'info' | 'default' = 'default'
  if (planning.status === 'active') statusColor = 'success'
  else if (planning.status === 'upcoming') statusColor = 'info'

  return (
    <Paper
      data-testid="planning-overview-card"
      onClick={() => navigate(`${location.pathname}?planningId=${planning.id}&tab=admin`)}
      role="button"
      tabIndex={0}
      variant="outlined"
      sx={{
        cursor: 'pointer',
        p: 1.25,
        '&:focus-visible': {
          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          navigate(`${location.pathname}?planningId=${planning.id}&tab=admin`)
        }
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={600} noWrap>
            {planning.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(planning.startDate).format('MMM D')} - {dayjs(planning.endDate).format('MMM D, YYYY')}
          </Typography>
        </Box>
        <ActionsCell
          planning={planning}
          onArchiveCompleted={onArchiveCompleted}
          onUnarchiveCompleted={onUnarchiveCompleted}
          setPlanningToDelete={setPlanningToDelete}
        />
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} mt={1} flexWrap="wrap">
        <Chip label={getPlanningOverviewStatusLabel(planning)} color={statusColor} size="small" />
        <Typography variant="caption" color="text.secondary">
          {planning.totalShiftTemplates} {t`templates`}
        </Typography>
      </Stack>
      <Stack gap={0.5} mt={1.5}>
        <LinearProgress
          variant="determinate"
          value={planning.completionRate}
          sx={{
            height: 8,
            borderRadius: 5,
            backgroundColor: 'grey.300',
            '& .MuiLinearProgress-bar': { backgroundColor: getPlanningOverviewCompletionColor(planning.completionRate) },
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {Math.round(planning.completionRate)}% ({planning.staffedShifts}/{planning.totalPossibleShifts})
        </Typography>
      </Stack>
    </Paper>
  )
}
